import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, TextField, Typography } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { DialogWrapper } from '../../shared/components/common/dialogWrapper';
import { ButtonWrapper } from '../../shared/components/formUtilities/useButton';
import Logo from '../../shared/components/common/logo';
import { SigningService } from '../../services/signingService';
import UaePassSignWithButton from '../../assets/images/uaepass_sign_with_button.svg';

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SESSION_KEY = (id) => `signing_token_${id}`;

// Our API's BadRequest responses serialize ModelState directly as a flat
// { message: [code] } dictionary (BaseController.CustomResult), not wrapped in
// an "errors" key. Framework-level errors (404s, etc.) use ProblemDetails
// instead, with title/errors — handle both shapes.
const extractErrorMessage = (err) => {
    const data = err?.response?.data;
    if (!data || typeof data !== 'object') return undefined;
    if (typeof data.message === 'string') return data.message;
    if (data.errors && typeof data.errors === 'object') {
        const firstMessage = Object.values(data.errors)[0]?.[0];
        if (firstMessage) return firstMessage;
    }
    const firstValue = Object.values(data)[0];
    return (Array.isArray(firstValue) ? firstValue[0] : undefined) || data.title;
};

const STATUS = {
    AUTH_NATIONAL_ID: 'auth_national_id',
    LOADING: 'loading',
    ACTIVE: 'active',
    COMPLETED: 'completed',
    REJECTED: 'rejected',
    ERROR: 'error',
    SIGN_ERROR: 'sign_error',
};

const SIGN_ERROR_COPY = {
    auth_cancelled: {
        body1: 'User cancelled the login.',
    },
    not_eligible: {
        body1: 'You are not eligible to access this service. Your account is either not upgraded or you have a visitor account.',
    },
    not_qualified: {
        body1: 'Your UAE PASS signature is not qualified. Please upgrade your account following instructions in the UAE PASS app.',
    },
    identity_mismatch: {
        body1: 'This Service is only for registered users, please contact Funding Souq in order to access the services.',
    },
};

const UNKNOWN_SIGN_ERROR_COPY = {
    body1: 'Something went wrong while verifying your identity. Please contact Funding Souq support for assistance.',
};

const DarkErrorCard = ({ body1 }) => (
    <Box className="flex flex-col items-center justify-center min-h-screen bg-[#f5f6f8] px-6">
        <Box className="w-full max-w-[520px]">
            <Logo disabledLink sx={{ width: 'auto', height: 56 }} classList="mx-auto !mb-8 flex justify-center" />
            <Box className="bg-[#1b3034] rounded-2xl px-10 py-9 text-left">
                <Typography className="!text-[#a8bfc4] !text-[15px] !leading-[1.7]">
                    {body1}
                </Typography>
            </Box>
            <Typography className="!text-[#9aa5b1] !text-[13px] !text-center !mt-6">
                © Funding Souq Limited · Regulated by the DFSA
            </Typography>
        </Box>
    </Box>
);

const UaePassSignButton = ({ loading, disabled, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={loading || disabled}
        className="relative shrink-0 -ml-2 disabled:opacity-70 disabled:cursor-not-allowed"
    >
        <img src={UaePassSignWithButton} alt="Sign with UAE PASS" className="h-10 w-auto block" />
        {loading && (
            <Box className="absolute inset-0 flex items-center justify-center">
                <CircularProgress size={20} />
            </Box>
        )}
    </button>
);

export const SignPage = () => {
    const { signerProcessId } = useParams();
    const [searchParams] = useSearchParams();
    const signErrorCode = searchParams.get('error');

    const [status, setStatus] = useState(STATUS.LOADING);
    const [info, setInfo] = useState(null);
    const [nationalId, setNationalId] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [numPages, setNumPages] = useState(null);
    const [renderedPages, setRenderedPages] = useState(() => new Set());
    const [pdfWidth, setPdfWidth] = useState(900);
    const [signing, setSigning] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const serviceRef = useRef(null);

    useEffect(() => {
        const updateWidth = () => setPdfWidth(Math.min(window.innerWidth - 64, 900));
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    const loadSigningInfo = useCallback(async (token) => {
        serviceRef.current = new SigningService(token);
        setStatus(STATUS.LOADING);

        try {
            const resp = await serviceRef.current.getSigningInfo(signerProcessId);
            const data = resp.data;
            if (data.status === 'Created' && data.pendingSignUrl) {
                window.location.href = data.pendingSignUrl;
                return;
            }
            setInfo(data);
            setNumPages(null);
            setRenderedPages(new Set());
            if (data.status === 'Created') {
                setStatus(STATUS.ACTIVE);
            } else if (data.status === 'Completed') {
                setStatus(STATUS.COMPLETED);
            } else {
                setStatus(STATUS.REJECTED);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY(signerProcessId));
                window.location.reload();
            } else {
                setStatus(STATUS.ERROR);
            }
        }
    }, [signerProcessId]);

    useEffect(() => {
        if (signErrorCode) {
            setStatus(STATUS.SIGN_ERROR);
            return;
        }

        const stored = sessionStorage.getItem(SESSION_KEY(signerProcessId));
        if (stored) {
            loadSigningInfo(stored);
            return;
        }

        let cancelled = false;
        new SigningService(null).startSession(signerProcessId)
            .then((resp) => {
                if (cancelled) return;
                const token = resp.data?.token;
                if (!token) {
                    setStatus(STATUS.ERROR);
                    return;
                }
                sessionStorage.setItem(SESSION_KEY(signerProcessId), token);
                loadSigningInfo(token);
            })
            .catch(() => { if (!cancelled) setStatus(STATUS.ERROR); });
        return () => { cancelled = true; };
    }, [signerProcessId, signErrorCode, loadSigningInfo]);

    const handleSubmitNationalId = async () => {
        if (!nationalId.trim()) return;
        setAuthError('');
        setAuthLoading(true);
        try {
            await serviceRef.current.submitNationalId(signerProcessId, nationalId.trim());
            const resp = await serviceRef.current.initiateSign(signerProcessId);
            const url = resp.data?.authUrl || resp.data?.pendingSignUrl;
            if (url) {
                window.location.href = url;
            } else {
                setAuthError('Something went wrong. Please try again.');
                setAuthLoading(false);
            }
        } catch (err) {
            setAuthError(extractErrorMessage(err) || 'Could not save Emirates ID. Please check the number and try again.');
            setAuthLoading(false);
        }
    };

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        setNumPages(n);
        setRenderedPages(new Set());
    }, []);

    const handlePageRenderSuccess = useCallback((pageNumber) => {
        setRenderedPages((prev) => {
            const next = new Set(prev);
            next.add(pageNumber);
            return next;
        });
    }, []);

    const isDocumentFullyRendered = numPages != null && renderedPages.size >= numPages;

    const handleSign = async () => {
        setSigning(true);
        try {
            const resp = await serviceRef.current.initiateSign(signerProcessId);
            if (resp.data?.requiresNationalId) {
                setStatus(STATUS.AUTH_NATIONAL_ID);
                setSigning(false);
                return;
            }
            const url = resp.data?.pendingSignUrl || resp.data?.authUrl;
            if (url) {
                window.location.href = url;
            } else {
                setSigning(false);
            }
        } catch (err) {
            if (err?.response?.status === 401) {
                sessionStorage.removeItem(SESSION_KEY(signerProcessId));
                window.location.reload();
                return;
            }
            setSigning(false);
        }
    };

    const handleCancelConfirm = async () => {
        setCancelling(true);
        try {
            await serviceRef.current.cancelSigning(signerProcessId);
            setCancelDialogOpen(false);
            setStatus(STATUS.REJECTED);
        } catch {
            setCancelling(false);
        }
    };

    if (status === STATUS.AUTH_NATIONAL_ID) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center gap-5 text-center">
                    <Logo />
                    <Box className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <BadgeOutlinedIcon className="!text-primary" fontSize="medium" />
                    </Box>
                    <Box>
                        <Typography variant="h6" className="!font-semibold">
                            Confirm your Emirates ID
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500 mt-1">
                            We don't have an Emirates ID on file for you yet. Enter it below — we'll confirm it against your UAE PASS account on the next step.
                        </Typography>
                    </Box>
                    <TextField
                        label="Emirates ID number"
                        placeholder="784-XXXX-XXXXXXX-X"
                        fullWidth
                        value={nationalId}
                        onChange={(e) => setNationalId(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmitNationalId()}
                        error={!!authError}
                        helperText={authError}
                        size="small"
                    />
                    <ButtonWrapper
                        label="Continue"
                        loading={authLoading}
                        onClick={handleSubmitNationalId}
                        disabled={!nationalId.trim()}
                        fullWidth
                    />
                </Box>
            </Box>
        );
    }

    if (status === STATUS.LOADING) {
        return (
            <Box className="flex items-center justify-center min-h-[60vh]">
                <CircularProgress />
            </Box>
        );
    }

    if (status === STATUS.SIGN_ERROR) {
        const copy = SIGN_ERROR_COPY[signErrorCode] || UNKNOWN_SIGN_ERROR_COPY;
        return <DarkErrorCard body1={copy.body1} />;
    }

    if (status === STATUS.ERROR) {
        return (
            <DarkErrorCard body1="This signing link is invalid or has expired. Please contact Funding Souq support if you need assistance." />
        );
    }

    if (status === STATUS.COMPLETED) {
        return (
            <Box className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-4">
                <Box className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center gap-3 text-center">
                    <Box className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-2">
                        <Typography variant="h4" className="!text-white">✓</Typography>
                    </Box>
                    <Typography variant="h5" className="!font-semibold">
                        Document signed
                    </Typography>
                    <Typography className="!text-gray-500">
                        You have already signed this document. You will receive a copy by email once all parties have completed signing.
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (status === STATUS.REJECTED) {
        return (
            <DarkErrorCard body1="User cancelled the signing process." />
        );
    }

    return (
        <Box className="flex flex-col min-h-screen bg-gray-50">
            <Box className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
                <Box className="max-w-[960px] mx-auto flex items-center justify-between gap-4">
                    <Box>
                        <Typography variant="subtitle1" className="!font-semibold leading-tight">
                            Finance Contract – Application #{info?.applicationId}
                        </Typography>
                        <Typography variant="body2" className="!text-gray-500">
                            {info?.businessName} · Please review the document below before signing
                        </Typography>
                    </Box>
                    <Box className="flex items-center gap-0 shrink-0">
                        <ButtonWrapper
                            variant="outlined"
                            label="Cancel"
                            classList="!text-red-500 !border-red-300 hover:!bg-red-50"
                            onClick={() => setCancelDialogOpen(true)}
                        />
                        <UaePassSignButton
                            loading={signing}
                            disabled={!isDocumentFullyRendered}
                            onClick={handleSign}
                        />
                    </Box>
                </Box>
            </Box>

            <Box className="flex-1 flex justify-center py-8 px-4 overflow-auto">
                {info?.documentUrl ? (
                    <Box>
                        <Document
                            file={info.documentUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            loading={
                                <Box className="flex justify-center py-16">
                                    <CircularProgress />
                                </Box>
                            }
                            error={
                                <Box className="flex justify-center py-16">
                                    <Typography className="!text-gray-400">
                                        Could not load document preview.
                                    </Typography>
                                </Box>
                            }
                        >
                            {numPages &&
                                Array.from({ length: numPages }, (_, i) => (
                                    <Box key={i + 1} className="mb-4 shadow-md">
                                        <Page
                                            pageNumber={i + 1}
                                            width={pdfWidth}
                                            renderTextLayer
                                            renderAnnotationLayer
                                            onRenderSuccess={() => handlePageRenderSuccess(i + 1)}
                                        />
                                    </Box>
                                ))}
                        </Document>
                    </Box>
                ) : (
                    <Box className="flex items-center justify-center h-64">
                        <Typography className="!text-gray-400">Document not available</Typography>
                    </Box>
                )}
            </Box>

            <DialogWrapper
                state={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                title="Cancel signing"
                maxWidth="sm"
                content={
                    <Box className="flex flex-col gap-3">
                        <Typography>
                            Are you ready to cancel this signing request?
                        </Typography>
                        <Typography className="!text-gray-500">
                            If you are not ready to sign right now, simply close this dialog — you can come back later using the same link from your email or SMS.
                        </Typography>
                        <Typography className="!text-red-500 !text-sm">
                            <strong>Cancel permanently</strong> will reject the entire signing process and cannot be undone.
                        </Typography>
                    </Box>
                }
                actions={
                    <>
                        <ButtonWrapper
                            variant="outlined"
                            label="Come back later"
                            onClick={() => setCancelDialogOpen(false)}
                        />
                        <ButtonWrapper
                            label="Cancel permanently"
                            loading={cancelling}
                            onClick={handleCancelConfirm}
                            classList="!bg-red-500 hover:!bg-red-600"
                        />
                    </>
                }
            />
        </Box>
    );
};
